import { useMemo } from 'react'

import i from 'i18next'

import Panel from '~/components/Panel'
import { FormGroup, Input, Label, Radio } from '~/components/unform'
import { useAdTypes } from '~/modules/retailMedia/hooks/useAdTypes'

import { getAdTypesOptions } from '../helpers'
import { useRequestAds } from '../hooks/useRequestAds'
import AdSizeFieldset from './AdSizeFieldset'
import BrandsFieldset from './BrandsFieldset'

const AdTypeSection = () => {
  const { type, setType, formRef, handleURLChange } = useRequestAds()

  const { data: adTypes = [] } = useAdTypes()

  const typeOptions = useMemo(() => getAdTypesOptions(adTypes), [adTypes])

  const handleChangeType = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value as CampaignAdType

    // Update local state for UI rendering
    setType(value)

    // Keep URL in sync (and clear URL params when needed)
    handleURLChange({ name: 'type', value })

    // Always reset `size` on type change to avoid invalid carry-over.
    formRef.current?.clearField('size')
  }

  const showAdSize = useMemo(
    () => ['banner', 'banner_video', 'banner_off_site'].includes(type),
    [type]
  )

  const showBrand = useMemo(
    () =>
      [
        'banner',
        'sponsored_brand',
        'banner_video',
        'sponsored_brand_video',
        'banner_off_site'
      ].includes(type),
    [type]
  )

  const isOffsiteTypeSelected = useMemo(
    () => type === 'banner_off_site',
    [type]
  )

  return (
    <Panel title={i.t('rm:Ads')}>
      <FormGroup>
        <Radio
          options={typeOptions}
          name="type"
          layout="grid"
          required
          itemSize="small"
          onChange={handleChangeType}
        />
      </FormGroup>

      <FormGroup className="d-flex justify-content-between mt-4">
        <Label className="w-75">
          {i.t('rm:placements.form.quantityAds.label')}
        </Label>
        <Input name="quantity" placeholder="Quantity" required />
      </FormGroup>

      {showAdSize && <AdSizeFieldset adType={type} />}

      {showBrand && !isOffsiteTypeSelected && <BrandsFieldset />}

      {/* If offsite type selected, show optional input for brand,
          because we dont know the destination publishers (if they have brands)
      */}
      {isOffsiteTypeSelected && (
        <FormGroup className="d-flex justify-content-between mt-2">
          <Label className="w-75">Brand for URL</Label>
          <Input name="brand" placeholder="Brand" />
        </FormGroup>
      )}
    </Panel>
  )
}

export default AdTypeSection
